import { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import ActionButton from '../actionButton';
import Tooltip from '../tooltip';
import CustomSwitch from '../customSwitch';
import { ReactComponent as PersonalAttributeIcon } from '../../../assets/icons/private-attribute-icon.svg';
import { ReactComponent as TextReplyIcon } from '../../../assets/icons/text_snippet.svg';
import { ReactComponent as AddCommentIcon } from '../../../assets/icons/add_comment_small.svg';
import { useTranslations } from '../../../utility/useTranslations';
import { getPersonalAttributeTooltipText } from '../../../utility/uiUtils';
import { APP_COMPONENTS } from '../../../constants/pages';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    boxSizing: 'border-box',
    backgroundColor: ({ color, isTextReply }) =>
      isTextReply || !color ? primary.bluish8 : color,
    display: 'flex',
    alignItems: 'center',
    padding: spacing(0, 2),
    height: 28,
    transition: 'background-color .25s ease',
    maxWidth: '100%',
    minWidth: 0,
    width: ({ hasCommentsSwitch }) => (hasCommentsSwitch ? '100%' : 'auto'),
  },
  selectableAttribute: {
    cursor: 'pointer',
  },
  hasRemove: {
    padding: spacing(0, 1, 0, 2),
  },
  attribute: {
    lineHeight: '20px',
    justifySelf: 'start',
    maxWidth: '100%',
  },
  textReplyAttribute: {
    border: ({ color }) => `4px solid ${color}`,
  },
  switch: {
    flexShrink: 0,
    marginLeft: spacing(1),
    width: 'auto',
  },
  button: {
    flexShrink: 0,
    width: 24,
    height: 24,
    marginLeft: spacing(1),
  },
  icon: {
    width: 16,
    height: 16,
  },
  attributeIcon: {
    cursor: 'default',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    marginLeft: spacing(1),
    marginTop: spacing(-0.25),
    '& svg': {
      '& g, & path': {
        fill: primary.bluish5,
      },
    },
    '& g, & path': {
      fill: primary.bluish5,
    },
  },
  attributeIconSelected: {
    '& svg': {
      '& g, & path': {
        fill: primary.bluish1,
      },
    },
    '& g, & path': {
      fill: primary.bluish1,
    },
  },
  dragButton: {
    width: 22,
    height: 22,
    flexShrink: 0,
    marginRight: spacing(1),
    '& button': {
      width: 22,
      height: 22,
    },
  },
  dragIcon: {
    width: 8,
    height: 14,
  },
  dragTooltipLabel: {
    fontFamily: 'ProximaNova-Regular',
  },
  previewButton: {
    width: 24,
    height: 24,
  },
  previewTooltip: {
    fontFamily: 'ProximaNova-Regular',
  },
}));

const AttributeChip = ({
  customClass = null,
  attributeNameClass = undefined,
  style = null,
  color = undefined,
  name = '',
  isTextReply = false,
  attributeNumber = 0,
  attribute = {},
  isSelectable = false,
  isSelected = false,
  isAttributeDraggable = false,
  hasAnswerComment = false,
  dragRef = () => {},
  dragProps = null,
  hasCommentsSwitch = false,
  hasPreview = false,
  hasRemove = false,
  onSelect,
  onRemove = () => {},
  onToggleComments = () => {},
  onPreview = () => {},
}) => {
  const classes = useStyles({ color, isTextReply, hasCommentsSwitch });
  const translations = useTranslations(APP_COMPONENTS.ATTRIBUTE_CHIP);

  const handleRemove = useCallback(
    () => onRemove(attribute),
    [attribute, onRemove]
  );

  const handleSelect = useCallback(() => {
    if (isSelectable) {
      onSelect(attribute);
    }
  }, [attribute, isSelectable, onSelect]);

  const handlePreview = useCallback(
    () => onPreview(attribute.id),
    [attribute, onPreview]
  );

  return (
    <div
      ref={dragRef}
      className={classNames(
        classes.root,
        {
          [classes.textReplyAttribute]: isTextReply,
          [classes.hasRemove]: hasRemove,
          [classes.selectableAttribute]: isSelectable,
        },
        customClass
      )}
      {...(style ? { style } : {})}
      {...(dragProps ? { ...dragProps.draggableProps } : {})}
      onClick={handleSelect}
    >
      {isAttributeDraggable && (
        <ActionButton
          className={classes.dragButton}
          iconClass={classes.dragIcon}
          tooltipLabelClass={classes.dragTooltipLabel}
          type={ACTION_BUTTON_TYPES.DRAG}
          tooltipText={translations.dragTooltip}
          {...(dragProps ? { ...dragProps.dragHandleProps } : {})}
        />
      )}
      <TextBoxWithTooltip
        variant="subtitle2"
        className={classNames(classes.attribute, attributeNameClass)}
        text={name}
        itemNumber={attributeNumber}
      />
      {hasAnswerComment && (
        <Tooltip
          customIconClass={classNames(classes.attributeIcon, {
            [classes.attributeIconSelected]: isSelected,
          })}
          text={translations.answerComments}
          icon={AddCommentIcon}
        />
      )}
      {attribute.text_reply_questions && (
        <Tooltip
          customIconClass={classNames(classes.attributeIcon, {
            [classes.attributeIconSelected]: isSelected,
          })}
          text={translations.openEndedAttribute}
          icon={TextReplyIcon}
        />
      )}
      {attribute.is_private && (
        <Tooltip
          customIconClass={classNames(classes.attributeIcon, {
            [classes.attributeIconSelected]: isSelected,
          })}
          text={
            attribute.created_by?.id
              ? getPersonalAttributeTooltipText(
                  translations.userPersonalAttribute,
                  attribute.created_by
                )
              : translations.personalAttribute
          }
          icon={PersonalAttributeIcon}
        />
      )}
      {hasCommentsSwitch && !attribute.text_reply_questions && (
        <CustomSwitch
          className={classes.switch}
          tooltipText={translations.toggleComments}
          isChecked={!!attribute?.has_answer_comment}
          switchIcon={AddCommentIcon}
          onChange={onToggleComments}
        />
      )}
      {hasPreview && (
        <ActionButton
          rootClass={classes.button}
          iconButtonClass={classes.previewButton}
          iconClass={classes.icon}
          tooltipLabelClass={classes.previewTooltip}
          type={ACTION_BUTTON_TYPES.PREVIEW_QUESTIONS}
          tooltipText={translations.preview}
          onClickHandler={handlePreview}
        />
      )}
      {hasRemove && (
        <ActionButton
          className={classes.button}
          iconClass={classes.icon}
          type={ACTION_BUTTON_TYPES.CLOSE}
          size="small"
          onClickHandler={handleRemove}
        />
      )}
    </div>
  );
};

AttributeChip.propTypes = {
  name: PropTypes.string,
  attributeNameClass: PropTypes.string,
  color: PropTypes.string,
  style: PropTypes.object,
  customClass: PropTypes.string,
  attribute: PropTypes.object,
  isTextReply: PropTypes.bool,
  hasCommentsSwitch: PropTypes.bool,
  hasRemove: PropTypes.bool,
  isSelectable: PropTypes.bool,
  isSelected: PropTypes.bool,
  isAttributeDraggable: PropTypes.bool,
  hasAnswerComment: PropTypes.bool,
  hasPreview: PropTypes.bool,
  attributeNumber: PropTypes.number,
  dragProps: PropTypes.object,
  dragRef: PropTypes.func,
  onRemove: PropTypes.func,
  onToggleComments: PropTypes.func,
  onPreview: PropTypes.func,
};

export default AttributeChip;
